import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { commonImages } from '@images/common-images';
import { VoiceRecognitionService } from '@modules/rmi-notes/voice-recognition.service';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import { ApiService } from '@services/api/api.service';
import { GlobalSearchService } from '@services/api/global-search.service';
import { NotesService } from '@services/api/notes.service';
import { AlertService } from '@services/helpers/alert.service';
import { ReduxService } from '@state-management/redux.service';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subject, debounceTime } from 'rxjs';

@Component({
  selector: 'app-notes',
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss'],
})
export class NotesComponent {
  @Input() moduleId: any = {};
  @Input() module: string = '';
  @Input() type: string = '';
  @Input() isListing: boolean = false;
  @Input() isView: boolean = false;
  @Output() pinnedNotes = new EventEmitter<true>();
  @Output() notesList = new EventEmitter<any>();

  constructor(
    private Api: ApiService,
    private toast: ToastrService,
    private alertService: AlertService,
    public voice: VoiceRecognitionService,
    private sanitizer: DomSanitizer,
    private notesService: NotesService,
    private _globalSearchService: GlobalSearchService,
    private redux: ReduxService,
    private remmiConfirm: RemmiConfirmService
  ) {}

  dateRange: any = [];
  sdRange: any = null;
  daterRange;
  searchText = null;
  common_images = commonImages;

  moment: any = moment;
  selected_moduleId = null;
  notes_list: any[] = [];
  isEdit = false;
  note_title = null;
  note_desc = null;
  note_detail = null;
  isLoad = false;

  ngOnChanges(changes: SimpleChanges): void {
    this.selected_moduleId = changes?.moduleId?.currentValue;
  }

  ngOnInit() {
    // Create an array of date ranges
    const today = new Date();
    this.dateRange.push(
      {
        id: 101,
        name: 'Prev week',
        d:
          moment(today).subtract(1, 'weeks').startOf('week').add(1, 'days').format('DD MMM YYYY') +
          ' - ' +
          moment(today).subtract(1, 'weeks').endOf('week').format('DD MMM YYYY'),
      },
      {
        id: 102,
        name: 'This week',
        d: moment(today).startOf('week').add(1, 'days').format('DD MMM YYYY') + ' - ' + moment(today).endOf('week').format('DD MMM YYYY'),
      },
      {
        id: 101,
        name: 'Prev month',
        d:
          moment(today).subtract(1, 'month').startOf('month').format('DD MMM YYYY') +
          ' - ' +
          moment(today).subtract(1, 'month').endOf('month').format('DD MMM YYYY'),
      },
      { id: 103, name: 'This month', d: moment(today).format('DD MMM YYYY') }
    );

    // Get all notes
    this.getAllNotes();

    // Set up a subject to listen for changes on the search input
    this.searchSubject = new Subject();
    this.searchSubject.pipe(debounceTime(800)).subscribe(rex => {
      // Filter the notes based on the search query
      this.filterNote(rex);
    });
  }

  clearFilter() {
    // Clear the selected date range
    this.sdRange = null;

    // Clear the date range selection
    this.daterRange = null;

    // Retrieve all notes
    this.getAllNotes();
  }

  getAllNotes = () => {
    // Fetch all user notes based on the current office ID and selected module ID
    this.redux.getUserNotes().subscribe(res => {
      let notes_list = JSON.parse(JSON.stringify(res.data));
      this.notes_list = notes_list
        .filter(f => f.id === this.selected_moduleId._id)
        .sort((a, b) => {
          return a?.isPin === b?.isPin ? 0 : a?.isPin === true ? -1 : 1;
        });
      this.notesList.emit(this.notes_list);
      this.pinnedNotes.emit(true);
    });
  };

  updateNote = (isPin = false) => {
    /**
     * Updates a note based on the changes made by the user
     */

    if (!this.note_detail?._id || !this.selected_moduleId?._id) {
      return;
    }

    this.note_detail['isPin'] = isPin;
    this.note_detail['id'] = this.selected_moduleId._id;
    this.note_detail['title'] = this.note_title;
    this.note_detail['desc'] = this.note_desc = this.custom_Editor?.nativeElement['innerHTML'];

    // Make a PUT request to the API to update the note
    this.notesService.updateUserNotes(this.note_detail._id, this.note_detail).subscribe(() => {
      // Display a success message to the user
      this.toast.success('Updated successfully');
      this.redux.setNotes(null);

      // Retrieve all notes again to reflect the changes
      this.getAllNotes();

      // Notify the parent component that the notes have been updated
      this.alertService.noteAddedUpdate(true);

      // Reset the note data
      this.clearNotes();
    });
  };

  clearNotes() {
    this.isNew = false;
    this.isEdit = false;
    this.note_title = null;
    this.note_desc = null;
    this.note_detail = null;
    this.isRecording && this.stopService();
  }

  deleteNote = note => {
    /**
     * Deletes a note based on the provided note ID
     * @param note Note object containing the ID of the note to be deleted
     */
    if (!note._id) {
      // If the note ID is not provided, do nothing
      return;
    }
    this.remmiConfirm
      .delete({ title: 'Delete Note?', message: 'This note will be permanently deleted.', subject: note.title })
      .subscribe(isConfirmed => {
        if (!isConfirmed) {
          return;
        }
        // Make a DELETE request to the API to delete the note
        this.notesService.deleteNote(note._id).subscribe(() => {
          // Display a success message to the user
          this.toast.success('Deleted successfully');
          this.redux.setNotes(null);
          // Retrieve all notes again to reflect the changes
          this.getAllNotes();
          // Notify the parent component that the notes have been updated
          this.alertService.noteAddedUpdate(true);
          // Reset the note data
          this.clearNotes();
        });
      });
  };

  editNote(note, isPin = false) {
    // Set the component to edit mode
    this.isEdit = true;
    // Copy the title, description and details of the note to be edited
    this.note_title = note.title;
    // Use the sanitizer to prevent XSS attacks
    this.note_desc = this.sanitizer.bypassSecurityTrustHtml(note.desc);
    this.note_detail = note;

    if (isPin == true) {
      this.updateNote(true);
    }
  }

  pinUnpinNotes(note, isPin = false) {
    this.note_title = note.title;
    this.note_desc = this.sanitizer.bypassSecurityTrustHtml(note.desc);
    this.note_detail = note;
    this.updateNote(isPin);
  }

  tm: any = null;
  isRecording: boolean = false;
  text: any = null;

  @ViewChild('custom_Editor', { static: false }) custom_Editor!: ElementRef;

  async startService(event) {
    event.stopPropagation();
    /**
     * Stops the current voice recognition service, starts a new one, and updates the editor with the recognized text.
     */
    this.voice.stop(); // Stop any ongoing voice recognition
    clearInterval(this.tm); // Clear any existing intervals
    this.isRecording = true; // Set the recording flag to true
    this.voice.start(); // Start the voice recognition service

    // Fetch existing text from the editor, removing any paragraph tags
    let existingText = this.custom_Editor.nativeElement['innerHTML']?.replace(/<\/?p>/g, '') || '';

    // Set an interval to periodically update the editor with the recognized text
    this.tm = setInterval(() => {
      if (this.voice.text) {
        // If there is recognized text
        // Update the editor with the existing and new recognized text, wrapped in paragraph tags
        this.custom_Editor.nativeElement['innerHTML'] = `<p>${existingText} ${this.voice.text}</p>`.trim();
      }
    }, 100); // Interval set to 100 milliseconds
  }

  stopService() {
    /**
     * Stops the voice recognition service, clears any existing intervals, resets the recognized text,
     * and sets the recording flag to false.
     */
    this.voice.stop(); // Stop the voice recognition service
    clearInterval(this.tm); // Clear any existing intervals
    this.voice.text = ''; // Reset the recognized text
    this.tm = null; // Reset the interval
    this.isRecording = false; // Set the recording flag to false
  }

  setStyle(style: string) {
    this.custom_Editor?.nativeElement?.focus();
    document?.execCommand(style, true, null);
  }

  sanitizeDesc(desc: string): string {
    return desc.replace(/<\/?p>/g, '').trim();
  }

  isNew = false;
  createNote() {
    /**
     * Creates a new note.
     * Checks if the selected module ID is present and if not, shows an error message.
     * If present, it creates a new note with the entered title, description, and module ID.
     * If the note is created successfully, it shows a success message, clears the note data,
     * and retrieves all the notes.
     * If there is an error, it shows an error message.
     */
    // if (!this.selected_moduleId?._id) {
    //   this.toast.error('Module ID is missing. Cannot create a note.');
    //   return;
    // }
    // Get the description from the editor
    this.note_desc = this.custom_Editor?.nativeElement['innerHTML'];
    // Create a new note with the entered title, description, and module ID
    const newNote = {
      title: this.note_title,
      desc: this.note_desc || '',
      isPin: false,
      isEdit: false,
      id: this.selected_moduleId?._id,
      type: this.note_type ? this.note_type : this.type ? this.type : '',
      office_id: this.Api.getOfficeId(),
      user_id: this.Api.getUserId(),
      is_archive: false,
    };

    if (!this.note_title) {
      this.toast.error('Please add a note title.');
      return;
    }

    // Post the new note to the API
    this.notesService.saveUserNotes(newNote).subscribe({
      // If the API call is successful, show a success message, clear the note data, and retrieve all the notes
      next: _ => {
        this.toast.success('Saved successfully');
        this.isNew = false;
        this.redux.setNotes(null);
        this.getAllNotes();
        this.clearNotes();
        this.modId = null;
        this.note_type = null;
      },
    });
  }

  onClickOutSideNotes(isClickOutSideNote: Event) {
    /**
     * Handles the click outside event to reset search-related properties.
     * @param isClickOutSideNote Event object containing the 'bol' property.
     */
    if (isClickOutSideNote['bol'] === false) {
      // Clear the search results
      this.notes_record_list = [];

      // Set the search state to false
      this.isSearchTrue = false;

      // Set the loading state to false
      this.isLoad = false;
    }
  }

  isSearchTrue: boolean = false;
  private searchSubject = new Subject<any>();
  notes_record_list: any[] = [];
  isSearching = false;
  onNoteSearch() {
    this.isSearching = true;
    this.searchSubject.next(this.note_title);
  }

  search_notes_type = [
    { type: 'property', icon: this.common_images?.home_icon, width: '17px' },
    { type: 'listing', icon: this.common_images?.listing_icon, width: '16px' },
    { type: 'contacts', icon: this.common_images?.phoneBook_icon, width: '20px', margin: '-2px' },
    { type: 'contract', icon: this.common_images?.contract_icon, width: '20px', margin: '-2px' },
    { type: 'projects', icon: this.common_images?.project_1, width: '20px', margin: '-2px' },
    { type: 'units', icon: this.common_images?.apartment_icon, width: '16px' },
    { type: 'tasks', icon: this.common_images?.task_icon, width: '20px', margin: '-2px' },
    { type: 'leads', icon: this.common_images?.lead_icon, width: '20px', margin: '-2px' },
    { type: 'eoi', icon: this.common_images?.cloud_icon, width: '17px' },
    { type: 'other' },
  ];

  filterNote = eve => {
    /**
     * Get the global search results
     * @param eve The event passed to the function
     */
    this._globalSearchService.getGlobalSearchEntityItems(eve).subscribe({
      next: response => {
        /**
         * Create an empty array to store the result
         */
        let bar: any[] = [];

        /**
         * Create a mapping object to define the keys for each type of data
         */
        const mapping = {
          propertyData: { type: 'property', titleKey: 'property_address' },
          listingData: { type: 'listing', titleKey: 'property_address' },
          contacts: { type: 'contacts', titleKey: 'full_name' },
          contracts: { type: 'contract', titleKey: 'property_address' },
          projects: { type: 'projects', titleKey: 'Project_Name' },
          units: { type: 'units', titleKey: 'lot_name' },
          tasks: { type: 'tasks', titleKey: 'title', extraKey: 'task_status' },
          leads: { type: 'leads', titleKey: 'full_name', extraKey: 'lead_type' },
          eoi: { type: 'eoi', titleKey: 'project_name', extraKey: 'status_reason', extra: 'lot_name' },
        };

        /**
         * Loop through the result and create an array of objects with the required data
         */
        response.data.forEach(f => {
          Object.keys(mapping).forEach(key => {
            if (f?.[key]?.length > 0) {
              f[key].forEach(g => {
                bar.push({
                  title:
                    g[mapping[key].titleKey] +
                    (mapping[key].extraKey
                      ? mapping[key].type == 'eoi'
                        ? `(${g[mapping[key].extraKey]}) - ${g[mapping[key].extra]}`
                        : `(${g[mapping[key].extraKey]})`
                      : ''),
                  id: g._id,
                  type: mapping[key].type,
                  icon: this.search_notes_type.find(item => item.type === mapping[key].type)?.icon || '',
                  width: this.search_notes_type.find(item => item.type === mapping[key].type)?.width || '',
                  margin: this.search_notes_type.find(item => item.type === mapping[key].type)?.margin || '',
                  isImg: true,
                });
              });
            }
          });
        });

        /**
         * Update the search_notes_type array with the data from the result
         */
        this.search_notes_type.forEach(el => {
          el['data'] = bar.filter(type => el.type === type.type) || [];
        });

        /**
         * Set the search bar with the updated data
         */
        this.notes_record_list = this.search_notes_type;
        console.log('notes_record_list >>:', this.notes_record_list);
        this.isLoad = this.notes_record_list.every(f => f.data.length === 0);
        this.isSearchTrue = this.notes_record_list.some(f => f.data.length > 0);
        this.isSearching = false;
      },
    });
  };

  modId = null;
  note_type = null;
  addNoteItem = item => {
    /**
     * Add the selected item to the note
     * @param item The selected item
     */
    this.note_title = item.title;
    this.modId = item.id;
    this.note_type = item.type;
    // Clear the search bar
    this.notes_record_list = [];
    this.isSearchTrue = false;
  };
}
